/**
 * La cella «Ritiro» del Portafoglio: mostra lo stato **e** lo cambia.
 *
 * Tre stati, ma se ne marca uno solo (modello deciso con Enrico il 18-08-2026):
 *
 * - **tassativa** — il ritiro è già fissato. **Non si marca: si deriva.** Se
 *   fosse un'opzione da spuntare esisterebbero tassative senza ritiro, cioè
 *   promesse scoperte; derivandola lo stato non può mentire.
 * - **standard** — si produce entro la data, il ritiro non è ancora fissato.
 * - **a chiamata** — riempie il magazzino, il ritiro lo chiederà il cliente.
 *
 * Il gesto marca la **politica** (standard o a chiamata) e può valere per la
 * singola commessa o per **tutto il cliente**: WONDERFOOD ritira a rate *come
 * cliente* e ha undici commesse aperte — marcarle una a una non farebbe
 * accumulare il lavoro, e le nuove arriverebbero non marcate.
 *
 * Perché era a zero su duecento: il meccanismo esisteva in M3 dal giro di giugno
 * ma **il gesto non era in nessuna interfaccia** — la funzione che scrive stava in
 * una vista non più raggiungibile, e la colonna per cliente esisteva in DB senza
 * essere esposta. Stesso pattern della spunta «materiale di consumo».
 */

import { useState } from "react";
import { CalendarCheck, ChevronDown, Loader2, PhoneCall, Truck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useM1RitiroCliente, useM1RitiroCommessa } from "@/hooks/useM1";
import { cn } from "@/lib/utils";
import type { M1OrdineRaggruppato } from "@/types/api";

function formatDataBreve(iso: string): string {
  return new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "short" });
}

export function RitiroCella({
  ordine,
  onApplicato,
}: {
  ordine: M1OrdineRaggruppato;
  /** Il messaggio da mostrare in pagina: chi marca un cliente deve sapere quante
   *  commesse ha toccato, altrimenti crede di averne cambiata una. */
  onApplicato: (messaggio: string) => void;
}) {
  const [aperto, setAperto] = useState(false);
  const [nota, setNota] = useState("");
  const perCommessa = useM1RitiroCommessa();
  const perCliente = useM1RitiroCliente();
  const inCorso = perCommessa.isPending || perCliente.isPending;

  function marca(modalita: "standard" | "a_chiamata", ambito: "commessa" | "cliente") {
    const body = { modalita_ritiro: modalita, nota: nota.trim() || null };
    const comune = {
      onSuccess: (r: { commesse_interessate: number; riferimento: string; stato_ritiro: string }) => {
        setAperto(false);
        setNota("");
        onApplicato(
          ambito === "cliente"
            ? `${r.riferimento}: ${modalita === "a_chiamata" ? "a chiamata" : "standard"} — ${r.commesse_interessate} commesse aperte toccate.`
            : `Commessa ${r.riferimento}: ${r.stato_ritiro === "tassativa" ? "resta tassativa, il ritiro è già fissato" : modalita === "a_chiamata" ? "a chiamata" : "standard"}.`,
        );
      },
    };
    if (ambito === "cliente") {
      perCliente.mutate({ codice: ordine.cliente_codice, body }, comune);
    } else {
      perCommessa.mutate({ numero: ordine.numero_ordine, body }, comune);
    }
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setAperto((v) => !v);
        }}
        className="group/rit flex items-center gap-1 rounded px-1 py-0.5 text-left hover:bg-muted"
        title="Cambia la politica di ritiro"
      >
        <Badge ordine={ordine} />
        <ChevronDown className="size-3 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover/rit:opacity-100" />
      </button>

      {aperto && (
        <>
          {/* Chiude cliccando fuori, senza libreria: il pannello è piccolo. */}
          <div
            className="fixed inset-0 z-40"
            onClick={(e) => {
              e.stopPropagation();
              setAperto(false);
            }}
          />
          <div
            className="absolute left-0 top-full z-50 mt-1 w-[19rem] space-y-2 rounded-lg border bg-popover p-3 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            {ordine.stato_ritiro === "tassativa" ? (
              <p className="rounded border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-[11px] leading-snug text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-200">
                Questa commessa è <b>tassativa</b>: il ritiro è già fissato
                {ordine.ritiro_fissato_il ? ` per il ${formatDataBreve(ordine.ritiro_fissato_il)}` : ""}.
                Lo stato non si marca — si ricava dal ritiro. Quello che scegli qui
                vale <b>se il ritiro salta</b>.
              </p>
            ) : (
              <p className="text-[11px] leading-snug text-muted-foreground">
                Marca la <b>politica</b>: diventa <b>tassativa</b> da sé quando il
                ritiro viene fissato.
              </p>
            )}

            <label className="block space-y-1">
              <span className="text-[10.5px] font-medium text-muted-foreground">
                Perché (resta come traccia)
              </span>
              <input
                value={nota}
                onChange={(e) => setNota(e.target.value)}
                placeholder="es. ritira a rate, ce l'ha chiesto lui"
                className="w-full rounded border bg-background px-2 py-1 text-xs"
              />
            </label>

            <div className="space-y-1.5">
              <Riga
                titolo="Solo questa commessa"
                azioni={[
                  {
                    label: "Standard",
                    onClick: () => marca("standard", "commessa"),
                    attivo:
                      ordine.modalita_ritiro === "standard" && ordine.ritiro_marcato_su_commessa,
                  },
                  {
                    label: "A chiamata",
                    onClick: () => marca("a_chiamata", "commessa"),
                    attivo:
                      ordine.modalita_ritiro === "a_chiamata" && ordine.ritiro_marcato_su_commessa,
                  },
                ]}
                inCorso={inCorso}
              />
              <Riga
                titolo={`Tutte le commesse di ${ordine.cliente_ragione_sociale}`}
                azioni={[
                  { label: "Standard", onClick: () => marca("standard", "cliente") },
                  { label: "A chiamata", onClick: () => marca("a_chiamata", "cliente") },
                ]}
                inCorso={inCorso}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Riga({
  titolo,
  azioni,
  inCorso,
}: {
  titolo: string;
  azioni: { label: string; onClick: () => void; attivo?: boolean }[];
  inCorso: boolean;
}) {
  return (
    <div className="space-y-1">
      <p className="truncate text-[10.5px] text-muted-foreground">{titolo}</p>
      <div className="flex gap-1.5">
        {azioni.map((a) => (
          <Button
            key={a.label}
            type="button"
            size="sm"
            variant={a.attivo ? "default" : "outline"}
            disabled={inCorso}
            onClick={a.onClick}
            className="h-7 flex-1 text-[11px]"
          >
            {inCorso ? <Loader2 className="size-3 animate-spin" /> : a.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

/**
 * Il badge dello stato **effettivo**. Tre forme distinte, perché tre stati che si
 * somigliano non si distinguono a colpo d'occhio in una tabella da 25 righe.
 */
export function Badge({ ordine }: { ordine: M1OrdineRaggruppato }) {
  if (ordine.stato_ritiro === "tassativa") {
    const scaduto = ordine.ritiro_scaduto;
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold",
          scaduto
            ? "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300"
            : "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
        )}
        title={
          scaduto
            ? "Il ritiro era fissato e la data è passata: o la merce è uscita e nessuno ha chiuso la commessa, o il ritiro è saltato"
            : "Il ritiro è fissato: la promessa è coperta. Stato derivato, non marcato."
        }
      >
        <CalendarCheck className="size-3" />
        {ordine.ritiro_fissato_il ? formatDataBreve(ordine.ritiro_fissato_il) : "fissato"}
        {scaduto && " ⚠"}
      </span>
    );
  }
  if (ordine.stato_ritiro === "a_chiamata") {
    return (
      <span
        className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-orange-100 px-2 py-0.5 text-[11px] font-semibold text-orange-800 dark:bg-orange-950 dark:text-orange-300"
        title="Il cliente ritira quando gli serve: la merce resta nel nostro magazzino"
      >
        <PhoneCall className="size-3" />
        a chiamata
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground"
      title="Si produce entro la data prevista, il ritiro non è ancora fissato"
    >
      <Truck className="size-3" />
      standard
    </span>
  );
}
